/**
 * Copyright (c) Microsoft. All rights reserved.
 */

/* eslint-disable jsx-a11y/role-supports-aria-props */
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
    IContextualMenuProps,
    IContextualMenuStyles
} from '@fluentui/react/lib/ContextualMenu';
import { IIconProps, IIconStyles, Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import { Property } from 'csstype';
import * as React from 'react';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { NestedCSSProperties, getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { LuisSidebarItemProps, LuisSidebarNormalItemProps } from 'src/core/types/luisSidebar';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

const { styleAnchor, styleComponent } = getStylist('LuisSidebarItem');

const itemTheme = getScopedTheme('LuisSidebarItem');

const focusStyles = mergeStyles(getFocusStyle(getTheme(), { inset: 1 }));

const IconContainer = styleComponent<IStackProps>(Stack)('IconContainer', theme => ({
    minWidth: theme.itemIconWidth,
    color: theme.itemIconColor
}));

const SidebarHeader = styleComponent<LuisTextProps>(LuisText)('SidebarHeader', theme => ({
    transition: 'all 0.3s ease',
    height: theme.itemLinkHeight,
    margin: '12px 0px -17px 18px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '180px',
    verticalAlign: 'center',
    display: 'inline-block'
}));

const SidebarItemLink = styleAnchor<{ isSelected: boolean; isChild: boolean; isChildWithIcon?: boolean }>(
    'SidebarItemLink',
    theme => ({
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        flexDirection: 'row',
        transition: 'all 0.3s ease',
        $nest: { '&:hover,&:focus': { backgroundColor: theme.itemBackgroundColorHovered } }
    }),
    ({ customProps }, theme) => {
        const heightStyles: NestedCSSProperties = { height: customProps.isChild ? theme.itemChildLinkHeight : theme.itemLinkHeight };
        const paddingStyles: NestedCSSProperties = { paddingLeft: customProps.isChildWithIcon ? '16px' : 'none' };
        const selectedStyles: NestedCSSProperties = customProps.isSelected ? { backgroundColor: theme.itemBackgroundColorSelected } : {};

        return { ...heightStyles, ...selectedStyles, ...paddingStyles };
    }
);

const contextualMenuStyles = classNamesFunction<IContextualMenuProps, IContextualMenuStyles>()({
    root: { backgroundColor: itemTheme.menuBackground }
});

const iconStyles = classNamesFunction<IIconProps, IIconStyles>()({ root: { height: 16 } });

const LuisSidebarItemNormal = React.forwardRef(
    (props: LuisSidebarNormalItemProps & { className?: string } & { index?: number }, ref: React.MutableRefObject<HTMLElement>) => {
        const {
            text,
            onClick,
            iconName,
            items = [],
            isSelected,
            isCollapsed,
            onRenderIcon,
            onRenderText,
            secondaryText,
            className = '',
            isChild = false,
            dataAutomationId,
            forceNoWrap = false,
            displayChildren = true,
            'aria-label': ariaLabel,
            id = '',
            index
        } = props;
        const [showMenu, setShowMenu] = React.useState(false);

        const hasSubItems = items.length > 0;
        const shouldDisplayChildrenList = hasSubItems && displayChildren && !isCollapsed;
        const shouldDisplayChildrenCallout = hasSubItems && isCollapsed;

        /**
         * Renders the main item content link for
         * this side bar item.
         *
         * - If the sidebar is collapsed, no text is shown
         * and a tooltip is used instead.
         *
         * - The icon is hidden if not passed in the props.
         */
        const renderParentLinkItem = () => {
            const fontWeight = (isSelected ? itemTheme.itemLinkFontWeightSelected : itemTheme.itemLinkFontWeight) as Property.FontWeight;
            const textStyles = forceNoWrap
                ? classNamesFunction<ITextProps, ITextStyles>()({
                      root: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                  })
                : {};

            const clickInteraction = () => {
                onClick();

                if (shouldDisplayChildrenCallout) {
                    setShowMenu(true);
                }
            };

            const linkItem = (
                <SidebarItemLink
                    role="tab"
                    tabIndex={index}
                    className={focusStyles}
                    onClick={clickInteraction}
                    aria-label={ariaLabel ?? text}
                    aria-selected={isSelected}
                    customProps={{ isSelected, isChild, isChildWithIcon: Boolean(isChild && iconName) }}
                    data-automation-id={dataAutomationId}
                    aria-haspopup={shouldDisplayChildrenCallout}
                    onKeyDown={e => e.key === LuisKeyCodes.Enter && clickInteraction()}
                    {...(shouldDisplayChildrenCallout ? { 'aria-expanded': showMenu } : {})}>
                    <IconContainer data-icon-container verticalFill verticalAlign="center" horizontalAlign="center">
                        {onRenderIcon?.(props) ?? (iconName && <Icon styles={iconStyles} iconName={iconName} />)}
                    </IconContainer>
                    {!isCollapsed &&
                        (onRenderText?.(props) ?? (
                            <LuisText fontWeight={fontWeight} styles={textStyles}>
                                {text}
                                {secondaryText && (
                                    <LuisText variant="smallPlus" styles={{ root: { fontStyle: 'italic' } }}>
                                        &nbsp;{secondaryText}
                                    </LuisText>
                                )}
                            </LuisText>
                        ))}
                </SidebarItemLink>
            );

            return (
                <LuisTooltip role="listitem" content={isCollapsed ? text : ''}>
                    {linkItem}
                </LuisTooltip>
            );
        };

        /**
         * Renders the sub items of this item as sidebar items.
         */
        const renderChildrenAsLinkItems = () =>
            items.map(item => <LuisSidebarItem key={item.text} {...item} isChild isCollapsed={isCollapsed} />);

        /**
         * Renders the items to be displayed in the contextual
         * menu if the side bar is collapsed.
         */
        const renderContextualMenuItems = (): IContextualMenuItem[] => [
            { key: text, text, onClick },
            ...items.map<IContextualMenuItem>(item => ({
                key: item.text,
                text: item.text,
                onClick: item.onClick
            }))
        ];

        return (
            <div id={id} ref={ref as any} className={`${className}`}>
                {renderParentLinkItem()}

                {shouldDisplayChildrenList && <Stack>{renderChildrenAsLinkItems()}</Stack>}

                {shouldDisplayChildrenCallout && (
                    <ContextualMenu
                        target={ref}
                        gapSpace={2}
                        beakWidth={0}
                        hidden={!showMenu}
                        isBeakVisible={false}
                        styles={contextualMenuStyles}
                        items={renderContextualMenuItems()}
                        onDismiss={() => setShowMenu(false)}
                        directionalHint={DirectionalHint.rightTopEdge}
                    />
                )}
            </div>
        );
    }
);

/**
 * Renders an item to be displayed in a LuisSidebar.
 */
export const LuisSidebarItem = React.forwardRef(
    (props: LuisSidebarItemProps & { className?: string } & { index?: number }, ref: React.MutableRefObject<HTMLElement>) => {
        if (props?.itemType === 'Header') {
            return (
                !props.isCollapsed && (
                    <SidebarHeader role="heading" aria-level={2} fontWeight={itemTheme.itemLinkFontWeightSelected as Property.FontWeight}>
                        {props.text}
                    </SidebarHeader>
                )
            );
        }
        return <LuisSidebarItemNormal ref={ref} {...props} />;
    }
);
